import { useCallback, useEffect, useRef, useState } from 'react'

interface Options {
  defaultWidth?: number
  minWidth?: number
  maxWidth?: number
  storageKey?: string
}

interface ResizableApi {
  width: number
  isDragging: boolean
  onMouseDown: (e: React.MouseEvent) => void
}

// Click-and-drag resize hook for sidebars. Returns the current width plus
// a mousedown handler to wire onto a drag handle. Width persists to
// localStorage when `storageKey` is supplied so it survives reloads.
//
// Constraints (`minWidth`/`maxWidth`) are clamped at the boundary so the
// sidebar can't shrink to zero or grow off-screen. The drag listeners live
// on `window` so the user can drag past the handle's own bounds without
// losing the gesture.
export function useResizable({
  defaultWidth = 280,
  minWidth = 200,
  maxWidth = 600,
  storageKey,
}: Options = {}): ResizableApi {
  const [width, setWidth] = useState<number>(() => {
    if (!storageKey) return defaultWidth
    try {
      const stored = localStorage.getItem(storageKey)
      if (stored != null) {
        const n = Number.parseInt(stored, 10)
        if (Number.isFinite(n) && n >= minWidth && n <= maxWidth) {
          return n
        }
      }
    }
    catch {
      /* localStorage unavailable */
    }
    return defaultWidth
  })

  const [isDragging, setIsDragging] = useState(false)
  const dragRef = useRef({ active: false, startX: 0, startWidth: 0 })

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    dragRef.current = { active: true, startX: e.clientX, startWidth: width }
    setIsDragging(true)
    // Lock the body's cursor so it doesn't flicker as the mouse crosses
    // child elements with their own cursor: rules.
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }, [width])

  useEffect(() => {
    function onMove(e: MouseEvent) {
      if (!dragRef.current.active) return
      const dx = e.clientX - dragRef.current.startX
      const next = Math.max(minWidth, Math.min(maxWidth, dragRef.current.startWidth + dx))
      setWidth(next)
    }
    function onUp() {
      if (!dragRef.current.active) return
      dragRef.current.active = false
      setIsDragging(false)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [minWidth, maxWidth])

  useEffect(() => {
    if (!storageKey) return
    try {
      localStorage.setItem(storageKey, String(width))
    }
    catch {
      /* ignore */
    }
  }, [width, storageKey])

  return { width, isDragging, onMouseDown }
}
